import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { SavedEstimateDetailsDto } from "src/models/estate/saved-estimate-details.model";
import { EvKeyValuePairModule } from "ev-key-value-pair";
import { ConvertUtil } from "src/util/tools/convert.util";
import { EVOwnership } from "src/models/units/unit-attributes.model";
import { KeyValuePair } from "../../../key-value-pair-list/key-value-pair-list.component";

@Component({
    selector: "saved-estimate-details",
    templateUrl: "./saved-estimate-details.component.html",
    styleUrls: ["./saved-estimate-details.component.scss", "../detail-views.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EvKeyValuePairModule],
})
export class SavedEstimateDetailsComponent {
    // Accept the key info data for the first column
    data = input<SavedEstimateDetailsDto | undefined>();

    protected Ownership = EVOwnership;

    protected keyValuePairs = computed<KeyValuePair[]>(() => {
        const info = this.data();
        if (!info) {
            return [];
        }
        return [
            this.createEstateOwnershipType(
                info.estateTypeDescription,
                info.ownershipDescription,
                info.estateType_ChangedToDescription,
                info.ownership_ChangedToDescription,
            ),
            this.createSiteType(info.siteTypeDescription, info.siteType_ChangedToDescription),
            this.createChangeLine("Tomtestørrelse", info.siteArea, info.siteArea_ChangedTo, "-", "m²", true),
            this.createChangeLine("Byggeår", info.buildYear, info.buildYear_ChangedTo, "-", "", false),
            this.createChangeLine("BRA-i", info.internalUsableArea, info.internalUsableArea_ChangedTo, "-", "m²", true),
            this.createChangeLine("P-Rom", info.livingArea, info.livingArea_ChangedTo, "-", "m²", true),
            this.createChangeLine("BRA", info.utilityFloorSpace, info.utilityFloorSpace_ChangedTo, "-", "m²", true),
            this.createChangeLine("BTA", info.grossArea, info.grossArea_ChangedTo, "-", "m²", true),
            this.createChangeLine("Soverom", info.bedrooms, info.bedrooms_ChangedTo, "-", "", false),
            this.createChangeLine("Etasje", info.floor, info.floor_ChangedTo, "-", "", false),
            this.createChangeLine("Andel fellesgjeld", info.currentCommonDebt, info.currentCommonDebt_ChangedTo, "-", "", true),
        ];
    });

    private createEstateOwnershipType(
        estateTypeDescription: string,
        ownershipDescription: string,
        estateType_ChangedToDescription: string,
        ownership_ChangedToDescription: string,
    ): KeyValuePair<string> {
        let value = estateTypeDescription + " " + ownershipDescription;
        let overrideValue = null;
        if (estateType_ChangedToDescription || ownership_ChangedToDescription) {
            overrideValue = `${estateType_ChangedToDescription ?? estateTypeDescription} ${ownership_ChangedToDescription ?? ownershipDescription}`;
        }
        return { key: "Boligtype", value: value, newValue: overrideValue, isOverridden: overrideValue !== null };
    }

    private createSiteType(siteTypeDescription: string, siteType_ChangedToDescription: string): KeyValuePair<string> {
        const hasOverride = !!siteType_ChangedToDescription;
        return {
            key: "Tomt",
            value: siteTypeDescription || "-",
            newValue: hasOverride ? siteType_ChangedToDescription : null,
            isOverridden: hasOverride,
        };
    }
    private createChangeLine(
        caption: string,
        originalValue: number | null | undefined,
        overrideValue: number | null | undefined,
        valueForNull = "-",
        postfix: string = "",
        thousandize: boolean,
    ): KeyValuePair<string | number> {
        const hasOverride = overrideValue !== null && overrideValue !== undefined;
        const formattedOverrideValue = hasOverride ? ConvertUtil.toNumberWithPostfix(overrideValue, valueForNull, postfix, thousandize) : null;
        return {
            key: caption,
            value: ConvertUtil.toNumberWithPostfix(originalValue ?? undefined, valueForNull, postfix, thousandize),
            newValue: formattedOverrideValue,
            isOverridden: hasOverride,
        };
    }
}
